import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, X, Tag, Hash } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { GalleryTag } from '@/lib/supabase';

interface TagManagerProps {
  isOpen: boolean;
  onClose: () => void;
  availableTags: GalleryTag[];
  selectedTags: GalleryTag[];
  onToggleTag: (tagId: string) => void;
  onCreateTag: (name: string, description?: string) => void;
  isCreatingTag?: boolean;
}

export const TagManager: React.FC<TagManagerProps> = ({
  isOpen,
  onClose,
  availableTags,
  selectedTags,
  onToggleTag,
  onCreateTag,
  isCreatingTag = false
}) => {
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { toast } = useToast();

  const selectedTagIds = selectedTags.map(tag => tag.id);

  const handleCreateTag = () => {
    if (!newTagName.trim()) {
      toast({ title: 'Tag name required', message: 'Please enter a name for the tag', type: 'error' });
      return;
    }

    onCreateTag(newTagName.trim(), newTagDescription.trim() || undefined);
    setNewTagName('');
    setNewTagDescription('');
    setShowCreateForm(false);
  };

  const masonyryServiceTags = [
    'Tuckpointing', 'Brick Repair', 'Chimney Repair', 'Stone Work', 
    'Restoration', 'Waterproofing', 'Crack Repair', 'Mortar Repair',
    'Heritage Restoration', 'Commercial Work', 'Residential Work'
  ];

  const quickCreateTags = masonyryServiceTags.filter(
    service => !availableTags.some(tag => 
      tag.name.toLowerCase() === service.toLowerCase()
    )
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="w-5 h-5" />
            Manage Gallery Tags
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Quick Create Common Tags */}
          {quickCreateTags.length > 0 && (
            <div>
              <Label className="text-sm font-medium mb-2 block">Quick Add Common Tags</Label>
              <div className="flex flex-wrap gap-2">
                {quickCreateTags.slice(0, 6).map(service => (
                  <Button
                    key={service}
                    variant="outline"
                    size="sm"
                    onClick={() => onCreateTag(service)}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {service}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Available Tags */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Available Tags</Label>
            {availableTags.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No tags available. Create your first tag below.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {availableTags.map(tag => {
                  const isSelected = selectedTagIds.includes(tag.id);
                  return (
                    <div key={tag.id} className="flex items-center space-x-2">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleTag(tag.id)}
                        id={`tag-${tag.id}`}
                      />
                      <Label
                        htmlFor={`tag-${tag.id}`}
                        className="flex-1 cursor-pointer flex items-center gap-2"
                      >
                        <Badge 
                          variant={isSelected ? "default" : "outline"}
                          className="text-xs"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag.name}
                        </Badge>
                        {tag.description && (
                          <span className="text-xs text-gray-500 truncate">
                            {tag.description}
                          </span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Tags Summary */}
          {selectedTags.length > 0 && (
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Selected Tags ({selectedTags.length})
              </Label>
              <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg">
                {selectedTags.map(tag => (
                  <Badge key={tag.id} className="bg-blue-100 text-blue-800">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag.name}
                    <button
                      onClick={() => onToggleTag(tag.id)}
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Create New Tag */}
          <div className="border-t pt-4">
            {!showCreateForm ? (
              <Button
                variant="outline"
                onClick={() => setShowCreateForm(true)}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Tag
              </Button>
            ) : (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Create New Tag</Label>
                <div>
                  <Label htmlFor="tag-name" className="text-xs">Tag Name</Label>
                  <Input
                    id="tag-name"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="e.g. Brick Repair, Commercial Work"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="tag-description" className="text-xs">Description (Optional)</Label>
                  <Textarea
                    id="tag-description"
                    value={newTagDescription}
                    onChange={(e) => setNewTagDescription(e.target.value)}
                    placeholder="Brief description of when to use this tag"
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateTag}
                    disabled={isCreatingTag || !newTagName.trim()}
                    size="sm"
                  >
                    {isCreatingTag ? 'Creating...' : 'Create Tag'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewTagName('');
                      setNewTagDescription('');
                    }}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};